import type { AxiosInstance } from "axios";
import axios from "axios";

export interface ApiResponse<T> {
  data: T;
}

export interface ValidateClientParams {
  clientId: string;
  redirectUri: string;
}

export interface ClientValidationResponse {
  isValid: boolean;
  client: {
    name: string;
    description?: string;
    logo?: string;
  };
}

export interface LoginParams {
  email: string;
  password: string;
  clientId: string;
  redirectUri: string;
}

export interface LoginResponse {
  requiresOtp: boolean;
  mfaToken: string;
  availableMethods: Array<"email" | "whatsapp">;
}

export interface RequestOtpParams {
  mfaToken: string;
  method: "email" | "whatsapp";
}

export interface RequestOtpResponse {
  sent: boolean;
  method: "email" | "whatsapp";
}

export interface VerifyOtpParams {
  mfaToken: string;
  otp: string;
  clientId: string;
  redirectUri: string;
  codeChallenge: string;
  codeChallengeMethod: string;
}

export interface VerifyOtpUserResponse {
  id: string;
  fullName: string;
  email: string;
  avatarUrl?: string | null;
}

export interface VerifyOtpResponse {
  redirectUrl: string;
  code: string;
  user: VerifyOtpUserResponse;
}

export interface AuthorizeParams {
  clientId: string;
  redirectUri: string;
  codeChallenge: string;
  codeChallengeMethod?: string;
  scope?: string;
  userId?: string;
  responseType: string;
}

export interface AuthorizeResponse {
  code: string;
  redirectUri: string;
}

export interface CheckSessionsResponse {
  validUserIds: string[];
}

export class AuthRemoteDataSource {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || "http://localhost:8000/sso",
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async validateClient(params: ValidateClientParams): Promise<ClientValidationResponse> {
    const response = await this.client.get<ApiResponse<ClientValidationResponse>>("/oauth2/validate", {
      params: {
        clientId: params.clientId,
        redirectUri: params.redirectUri,
      },
    });
    return response.data.data;
  }

  async login(params: LoginParams): Promise<LoginResponse> {
    const response = await this.client.post<ApiResponse<LoginResponse>>("/oauth2/login", {
      email: params.email,
      password: params.password,
      clientId: params.clientId,
      redirectUri: params.redirectUri,
    });
    return response.data.data;
  }

  async requestOtp(params: RequestOtpParams): Promise<RequestOtpResponse> {
    const response = await this.client.post<ApiResponse<RequestOtpResponse>>("/oauth2/otp/request", {
      mfaToken: params.mfaToken,
      method: params.method,
    });
    return response.data.data;
  }

  async verifyOtp(params: VerifyOtpParams): Promise<VerifyOtpResponse> {
    const response = await this.client.post<ApiResponse<VerifyOtpResponse>>("/oauth2/otp/verify", {
      mfaToken: params.mfaToken,
      otp: params.otp,
      clientId: params.clientId,
      redirectUri: params.redirectUri,
      codeChallenge: params.codeChallenge,
      codeChallengeMethod: params.codeChallengeMethod,
    });
    return response.data.data;
  }

  async authorize(params: AuthorizeParams): Promise<AuthorizeResponse> {
    const response = await this.client.post<{ data: AuthorizeResponse }>("/oauth2/authorize", params, { withCredentials: true });
    return response.data.data;
  }

  async checkSessions(userIds: string[]): Promise<CheckSessionsResponse> {
    const response = await this.client.post<ApiResponse<CheckSessionsResponse>>(
      "/oauth2/sessions/check",
      { userIds },
      { withCredentials: true },
    );
    return response.data.data;
  }
}

export const authRemoteDataSource = new AuthRemoteDataSource();
